// liveEval.js — what the live console runs, and whether it can.
// -----------------------------------------------
// Pure text work, no DOM: splitting the editor into expressions and checking
// each one against the parser before it is dispatched. Kept apart from
// oscillaLive.js so the rules can be tested without a browser.

import { parseCueToAST, parseUseDirective } from "../parser/parser.js";
import { splitCueId, useUid } from "../parser/cueUtils.js";

/**
 * A line with its `// comment` removed. A `//` inside quotes is text
 * (`text(content:"a // b")`), and so is the `//` of a URL in quotes.
 */
export function stripComment(line) {
  let quote = "";
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quote) {
      if (ch === quote) quote = "";
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === "/" && line[i + 1] === "/") {
      return line.slice(0, i);
    }
  }
  return line;
}

/**
 * The editor text as a list of expressions, each with the lines it spans:
 *   [{ expr, startLine, endLine }]
 *
 * An expression runs on to the next line while a bracket — ( [ or { — is
 * still open, so a long synth() can be laid out one parameter per line.
 * Brackets inside quotes do not count. A blank line always ends an
 * expression: a forgotten `)` then spoils one block, not everything below.
 * Lines starting with `//` or `#` are comments.
 */
export function scanExpressions(text) {
  const lines = text.split("\n");
  const out = [];
  let buf = "";
  let start = -1;
  let depth = 0;
  let quote = "";

  const flush = (endLine) => {
    if (buf) out.push({ expr: buf, startLine: start, endLine });
    buf = ""; start = -1; depth = 0; quote = "";
  };

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) { flush(i - 1); continue; }
    if (trimmed.startsWith("//") || trimmed.startsWith("#")) {
      if (depth <= 0) flush(i - 1);
      continue;
    }

    const code = stripComment(trimmed).trim();
    if (!code) continue;

    if (!buf) start = i;
    buf = buf ? buf + " " + code : code;

    for (const ch of code) {
      if (quote) { if (ch === quote) quote = ""; continue; }
      if (ch === '"' || ch === "'") quote = ch;
      else if (ch === "(" || ch === "[" || ch === "{") depth++;
      else if (ch === ")" || ch === "]" || ch === "}") depth--;
    }

    if (depth <= 0 && !quote) flush(i);
  }
  flush(lines.length - 1);
  return out;
}

/** The expression the given line belongs to, or null. */
export function expressionAtLine(text, line) {
  return scanExpressions(text).find(e => line >= e.startLine && line <= e.endLine) || null;
}

/** Brackets ( [ { still open at the end of `code` (quotes skipped). */
function openBrackets(code) {
  let depth = 0, quote = "";
  for (const ch of code) {
    if (quote) { if (ch === quote) quote = ""; continue; }
    if (ch === '"' || ch === "'") quote = ch;
    else if (ch === "(" || ch === "[" || ch === "{") depth++;
    else if (ch === ")" || ch === "]" || ch === "}") depth--;
  }
  return depth;
}

/**
 * The expression the caret is in, from its first line to the caret:
 *   { before, line, depth, indent }
 * A line carries on the expression above it while a bracket opened there is
 * still open (as scanExpressions reads it), so `before` is those lines
 * joined as it joins them, then the caret's line as typed. `line` is the
 * caret's line alone, `depth` the brackets open at the caret, `indent` the
 * expression's first line's indentation.
 */
export function expressionAtCaret(text, pos) {
  const lineStart = pos === 0 ? 0 : text.lastIndexOf("\n", pos - 1) + 1;
  const line = text.slice(lineStart, pos);
  let before = line, firstLine = lineStart;
  if (lineStart > 0) {
    const above = text.slice(0, lineStart - 1);
    const lines = above.split("\n");
    const exprs = scanExpressions(above);
    const last = exprs[exprs.length - 1];
    if (last && last.endLine === lines.length - 1 && openBrackets(last.expr) > 0) {
      before = last.expr + " " + line;
      firstLine = lines.slice(0, last.startLine).reduce((n, l) => n + l.length + 1, 0);
    }
  }
  return {
    before,
    line,
    depth: Math.max(0, openBrackets(stripComment(before))),
    indent: /^[ \t]*/.exec(text.slice(firstLine))[0],
  };
}

/**
 * Split `target > expr` into its two halves. Only a left side without
 * brackets counts as a target, so `>` inside an expression stays put.
 */
export function splitInlineTarget(line) {
  const gt = line.indexOf(">");
  if (gt > 0) {
    const before = line.slice(0, gt).trim();
    const after = line.slice(gt + 1).trim();
    if (before && after && !/[(\[{"']/.test(before)) return { target: before, expr: after };
  }
  return { target: null, expr: line };
}

const PUNCT = {
  LParen: "(", RParen: ")", LBrace: "{", RBrace: "}",
  LBracket: "[", RBracket: "]", Colon: ":", Comma: ",",
};

/**
 * One readable sentence from a chevrotain error, and where it points.
 * Chevrotain lists every token sequence it would have accepted; on stage
 * only the offending word and the bracket it wanted are any use.
 */
function describeParseError(e, expr) {
  const tok = e?.token;
  const atEnd = !tok?.image || tok.tokenType?.name === "EOF";
  const col = atEnd ? expr.length : tok.startOffset;
  const near = atEnd ? "the end" : `"${tok.image}"`;

  // gui() was renamed viz(): a set kept from before is told what to change
  if (!atEnd && col === 0 && tok.image === "gui") return { message: "gui() is now viz()", col };
  if (!atEnd && col === 0) return { message: `unknown cue "${tok.image}"`, col };
  const wanted = String(e?.message || "").match(/Expecting token of type --> (\w+) <--/)?.[1];
  if (wanted) {
    const what = PUNCT[wanted] || wanted;
    return { message: atEnd ? `missing ${what}` : `expected ${what} before ${near}`, col };
  }
  return { message: `unexpected ${near}`, col };
}

/**
 * A block's lines in the order they run: a level() fading a sound in goes
 * first. Run before the sound it waits for it, and brings it in from
 * silence (level.js); run after, the sound has started at its own level
 * and is heard for a moment before the fader comes down to begin the fade.
 */
export function fadesFirst(entries) {
  const fadesIn = ({ expr }) => {
    const { parts, error } = checkExpression(String(expr ?? "").replace(/\s+/g, " ").trim());
    if (error || parts.length !== 1 || parts[0].ast.type !== "cueLevel") return false;
    const args = parts[0].ast.args || [];
    const arg = t => args.find(a => a.type === t)?.value;
    const nums = arg("numbers") || [];
    const to = arg("to") ?? nums[0], dur = arg("dur") ?? nums[1];
    return typeof to === "number" && to > 0 && Number(dur) > 0;
  };
  // (checkExpression parses quietly: a line that will not parse says so when it runs)
  const first = entries.filter(fadesIn);
  return first.length ? [...first, ...entries.filter(e => !first.includes(e))] : entries;
}

/**
 * Check every cue in an expression before any of it runs:
 *   { parts: [{ expr, ast }] }
 * or
 *   { error: { message, expr, col } }
 *
 * A composite (`audio(...) rotate(...)`) is checked part by part, so one
 * bad part stops the whole line rather than half of it playing.
 */
export function checkExpression(expr) {
  const parts = [];
  for (const part of splitCueId(expr)) {
    if (!/^[A-Za-z_][\w]*\s*\(/.test(part)) {
      return { error: { message: `not a cue: "${part}"`, expr: part, col: 0 } };
    }
    let ast;
    try {
      // use(): a directive to the score's loader, a block on the stage here
      // quiet: what failed goes to the console's output, as the error returned here
      ast = /^use\s*\(/.test(part) ? parseUseDirective(part) : parseCueToAST(part, { quiet: true });
    } catch (err) {
      const first = err?.parseErrors?.[0];
      const { message, col } = first
        ? describeParseError(first, part)
        : { message: String(err?.message || err), col: 0 };
      return { error: { message, expr: part, col } };
    }
    if (!ast) {
      return { error: { message: `${part.split("(")[0]}() only works inside a score`, expr: part, col: 0 } };
    }
    if (ast.type === "cueUnknown") {
      return { error: { message: `unknown cue: ${part.split("(")[0]}`, expr: part, col: 0 } };
    }
    parts.push({ expr: part, ast });
  }
  if (!parts.length) return { error: { message: "nothing to run", expr, col: 0 } };
  return { parts };
}

// =================================================================
//  SOUNDS OWNED BY THE TEXT
//  A sound cue run from the console without a uid: would start a new,
//  nameless voice on every run, and the earlier ones could never be
//  reached again. So the console names it once, in the text itself
//  (synth(...) -> synth(uid:s1, ...)): from then on a re-run updates
//  that voice, and the name moves with the line through every edit.
// =================================================================

/** Cues that make what the rest of their line acts on: shape(...) rotate(...). */
export const LINE_LEADERS = new Set(["cueShape", "cueUse"]);

/** Cues that are worth naming -- sounds, and shapes -- and how their names start. */
const NAME_PREFIX = { cueSynth: "s", cueAudio: "a", cueAudioPool: "p", cueAudioImpulse: "i", cueShape: "sh" };

/** The uid the author wrote, if any. audio() falls back to its src as a
 *  uid, which is not a name: change the file and it is a second voice. */
/** adc(off:1) and the like: a line that already takes its thing away. */
const isOff = ast => ast?.args?.some(a => a.type === "off" && !/^(0|false|no)$/i.test(String(a.value)));

function uidOf(ast) {
  return ast?.args?.find(a => a.type === "uid")?.value ?? ast?.params?.uid
    ?? (ast?.type === "cueAudio" ? null : ast?.uid) ?? null;
}

/**
 * The top-level calls in a stretch of editor text, in order:
 * [{ name, open }] where `open` is the index of the call's "(".
 * Quotes and // comments are skipped, nested calls (rand(...)) are not
 * top-level. These line up one-to-one with splitCueId's parts.
 */
export function topLevelCalls(text) {
  const calls = [];
  let depth = 0, quote = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quote) { if (ch === quote) quote = ""; continue; }
    if (ch === '"' || ch === "'") { quote = ch; continue; }
    if (ch === "/" && text[i + 1] === "/") { const nl = text.indexOf("\n", i); if (nl < 0) break; i = nl; continue; }
    if (ch === "(") {
      if (depth === 0) {
        const name = text.slice(0, i).match(/([A-Za-z_]\w*)\s*$/)?.[1];
        if (name) calls.push({ name, open: i });
      }
      depth++;
    } else if (ch === ")") depth = Math.max(0, depth - 1);
  }
  return calls;
}

/**
 * Where to write uids into a block that is about to run:
 * [{ at, insert, uid }], `at` an index into blockText. `parts` are the
 * checked parts of the block (checkExpression), `fullText` the whole
 * editor, so a new name is one the text does not already use.
 */
export function planUids(blockText, parts, fullText) {
  const calls = topLevelCalls(blockText);
  if (calls.length !== parts.length) return [];
  const used = new Set([...fullText.matchAll(/\buid\s*:\s*["']?([\w-]+)/g)].map(m => m[1]));
  const plan = [];
  // on a shape's line the sounds are the shape's: the dispatcher names them
  // after it (a cluster: one per member), so they are left unnamed here
  const shapeLed = LINE_LEADERS.has(parts[0]?.ast.type);
  parts.forEach((part, i) => {
    const prefix = NAME_PREFIX[part.ast.type];
    if (!prefix || uidOf(part.ast) != null) return;
    if (shapeLed && i > 0) return;
    let n = 1;
    while (used.has(prefix + n)) n++;
    const uid = prefix + n;
    used.add(uid);
    const empty = /^\(\s*\)/.test(blockText.slice(calls[i].open));
    plan.push({ at: calls[i].open + 1, insert: empty ? `uid:${uid}` : `uid:${uid}, `, uid });
  });
  return plan;
}

/** The comment block around `line` with its // or # taken off, or "". */
export function commentedCode(text, line) {
  const lines = text.split("\n");
  const isComment = l => /^\s*(\/\/|#)/.test(l ?? "");
  if (!isComment(lines[line])) return "";
  let a = line, b = line;
  while (a > 0 && isComment(lines[a - 1])) a--;
  while (b < lines.length - 1 && isComment(lines[b + 1])) b++;
  return lines.slice(a, b + 1).map(l => l.replace(/^\s*(\/\/|#)\s?/, "")).join("\n");
}

// =================================================================
//  SETTLED RANDOMNESS
//  A line run on the laptop is also run by the projector window. Each
//  would roll its own rand() -- a triangle in two places, a scope at a
//  pitch nobody hears. So the line's rand(a, b), irand(a, b), birand(a, b)
//  rand([list]) and choose(a, b, ...) are rolled once, before it runs, and the same numbers
//  go to both. Not inside a pattern (Pseq, Prand ...): those draw anew on
//  every step, which is the point of them.
//
// Nor say()'s voice: and speaker: -- speaker:irand(0, 903) is a new person
// for every phrase it says (say.js rolls them), not one picked now. Nor a
// pool's or an impulse's amp:, pan:, speed: and fades: a draw for each sound.
// =================================================================

const RANDOMS = new Set(["rand", "irand", "birand", "choose"]);
/** cue -> the keys whose random calls that cue rolls itself, each time it uses them */
// viz(s3, spin:birand(10, 30)): a draw for each voice (scope.js), not one for the line;
// text(..., size:rand(18, 60), weight:choose(300, 900), blur:rand(0, 40)): one for each word (text.js);
// audioPool / audioImpulse(..., speed:rand(0.5, 2)): one for each sound they play (audioPool.js, audioImpulse.js)
const HIT = new Set(["amp", "pan", "speed", "pitch", "fade", "fadein", "fadeout", "fadeIn", "fadeOut"]);
const ROLLED_PER_USE = { say: new Set(["voice", "speaker"]), viz: new Set(["spin"]), text: new Set(["size", "weight", "blur"]), audioPool: HIT, audioImpulse: HIT };
const fmt = v => (Number.isInteger(v) ? String(v) : String(+v.toFixed(4)));

/** One roll of a random call given its name and argument text, or null. */
function roll(name, args, random) {
  const list = args.match(/^\s*\[(.*)\]\s*$/s);
  if ((list && name === "rand") || name === "choose") {
    const items = (list ? list[1] : args).split(",").map(s => s.trim()).filter(Boolean);
    return items.length ? items[Math.floor(random() * items.length)] : null;
  }
  const nums = args.split(",").map(s => s.trim());
  if (nums.length !== 2 || nums.some(n => !/^-?\d+(\.\d+)?$/.test(n))) return null;
  const [a, b] = nums.map(Number);
  const r = a + random() * (b - a);
  if (name === "irand") return fmt(Math.floor(r));
  if (name === "birand") return fmt(random() < 0.5 ? -r : r);
  return fmt(r);
}

/** The expression with its top-level random calls rolled into values. */
export function settleRandom(expr, random = Math.random) {
  let out = "", i = 0;
  const stack = [];                      // names of the calls we are inside
  let quote = "";
  while (i < expr.length) {
    const ch = expr[i];
    if (quote) { out += ch; if (ch === quote) quote = ""; i++; continue; }
    if (ch === '"' || ch === "'") { quote = ch; out += ch; i++; continue; }
    const m = /^([A-Za-z_]\w*)\s*\(/.exec(expr.slice(i));
    if (m) {
      const name = m[1];
      const inPattern = stack.some(n => /^P[a-z]/.test(n) || n === "hrand");
      // the value of a key its cue rolls itself: say(a1, speaker:irand(0, 903))
      const key = /([A-Za-z_]\w*)\s*:\s*$/.exec(out)?.[1];
      const cue = stack[stack.length - 1];
      const perUse = key && ROLLED_PER_USE[cue]?.has(key);
      if (RANDOMS.has(name) && !inPattern && !perUse) {
        // find the matching ")"
        let depth = 0, j = i + m[0].length - 1;
        for (; j < expr.length; j++) {
          if (expr[j] === "(") depth++;
          else if (expr[j] === ")" && --depth === 0) break;
        }
        const v = roll(name, expr.slice(i + m[0].length, j), random);
        if (v != null) { out += v; i = j + 1; continue; }
      }
      stack.push(name);
      out += m[0]; i += m[0].length; continue;
    }
    if (ch === "(") stack.push("");
    else if (ch === ")") stack.pop();
    out += ch; i++;
  }
  return out;
}

/**
 * tempo(120) -> tempo(120, beat:N): the beat the change starts on, chosen
 * here, so this window and the projector change tempo on the same one.
 */
export function settleTempo(expr, beat) {
  return expr.replace(/\btempo\s*\(([^()]*)\)/g, (all, inner) =>
    !inner.trim() || /\bbeat\s*:/.test(inner) ? all : `tempo(${inner.trim()}, beat:${beat})`);
}

/**
 * The tempo changes in a settled line, [{ bpm, beat }]: what the room is
 * told (roomBeat.js). tempo() with no bpm, or no beat yet, is none.
 */
export function tempoChangesIn(expr) {
  const out = [];
  for (const [, inner] of String(expr).matchAll(/\btempo\s*\(([^()]*)\)/g)) {
    const bpm = Number((/\bbpm\s*:\s*([-\d.]+)/.exec(inner) ?? /^\s*([-\d.]+)\s*(?:,|$)/.exec(inner))?.[1]);
    const beat = Number(/\bbeat\s*:\s*([-\d.]+)/.exec(inner)?.[1]);
    if (bpm > 0 && Number.isFinite(beat)) out.push({ bpm, beat });
  }
  return out;
}

// =================================================================
//  TAKING A LINE BACK
//  What stops what a line started: run for the line under the cursor
//  (Ctrl+Shift+.) or for commented-out code (comment it out, run it).
//  All of it DSL, so the projector window takes it back too.
// =================================================================

const SOUND_TYPES = new Set(["cueSynth", "cueAudio", "cueAudioPool", "cueAudioImpulse"]);
const ANIM_NAMES = { cueRotate: "rotate", cueScale: "scale", cueTrans: "trans", cueColor: "color", cueO2P: "o2p" };

/** The arguments of a call at its top level (a list or a call inside has commas of its own). */
function topLevelArgs(inner) {
  const parts = [];
  let depth = 0, from = 0;
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i];
    if (c === "[" || c === "(" || c === "{") depth++;
    else if (c === "]" || c === ")" || c === "}") depth--;
    else if (c === "," && depth === 0) { parts.push(inner.slice(from, i)); from = i + 1; }
  }
  parts.push(inner.slice(from));
  return parts;
}

/**
 * hold(s, 1) <-> hold(s, 0), gate(s, 0) <-> gate(s, 1), seq(s, 0) <-> seq(s, 1): the line run, the
 * console turns its switch round, so the next Ctrl+Enter does the other
 * thing -- the line shows what pressing it will do. hold(s) (on) and
 * gate(s) (open) become hold(s, 0), gate(s, 0); what follows the switch
 * (gate(s, 0, 4)) stays. null: not one.
 */
export function flipToggle(text) {
  // strobe(8) <-> strobe(8, off:1) -- bar > strobe(16) too: the line run, it says what the next press does
  const st = /^(\s*(?:[A-Za-z_][\w-]*\s*>\s*)?)strobe(\s*\()([^]*)\)(\s*(?:\/\/.*)?)$/.exec(text);
  if (st) {
    const [, lead, open, inner, tail] = st;
    const parts = topLevelArgs(inner);
    if (parts.some(p => /^\s*off\s*$/.test(p))) return null;          // strobe(off): it only stops
    const i = parts.findIndex(p => /^\s*off\s*:\s*1\s*$/.test(p));
    if (i >= 0) { parts.splice(i, 1); return `${lead}strobe${open}${parts.join(",")})${tail}`; }
    return `${lead}strobe${open}${inner}${inner.trim() ? ", " : ""}off:1)${tail}`;
  }
  const m = /^(\s*)(hold|gate|seq)(\s*\()([^]*)\)(\s*(?:\/\/.*)?)$/.exec(text);
  if (!m) return null;
  const [, lead, name, open, inner, tail] = m;
  // the arguments at the top level (a list of names has commas of its own)
  const parts = [];
  let depth = 0, from = 0;
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i];
    if (c === "[" || c === "(" || c === "{") depth++;
    else if (c === "]" || c === ")" || c === "}") depth--;
    else if (c === "," && depth === 0) { parts.push(inner.slice(from, i)); from = i + 1; }
  }
  parts.push(inner.slice(from));
  if (!parts[0].trim() || parts[0].includes(":")) return null;      // hold(uid:s): written out, leave it
  const sw = parts[1]?.trim();
  if (sw === "0" || sw === "1") parts[1] = parts[1].replace(sw, sw === "1" ? "0" : "1");
  else if (sw == null || sw.includes(":")) parts.splice(1, 0, " 0");   // hold(s) is on, gate(s) open: next, off
  else return null;
  return `${lead}${name}${open}${parts.join(",")})${tail}`;
}

/**
 * The synths a line starts, by name: synth(uid:s6, ...) -> ["s6"]. What
 * Ctrl+_ freezes (hold) and lets go again.
 */
export function synthsOf(line) {
  const { expr } = splitInlineTarget(line.replace(/\s+/g, " ").trim());
  const checked = checkExpression(expr);
  if (checked.error) return [];
  return checked.parts.filter(p => p.ast.type === "cueSynth").map(p => uidOf(p.ast)).filter(Boolean);
}

/** The animation cues a line can start, by the kind they register as. */
const ANIM_KINDS = { cueScale: "scale", cueRotate: "rotate", cueTrans: "trans", cueColor: "color", cueFade: "fade", cueO2P: "o2p" };

/**
 * The animations a line starts, and on what -- what Ctrl+_ stops where they
 * are (hold) and lets go on: { target, anims: [{ kind, uid }] }.
 *   bass > scale(uid:bsc, ...)         -> { target: "bass", anims: [{ kind: "scale", uid: "bsc" }] }
 *   shape(uid:sh1) rotate(dur:4)       -> { target: "sh1",  anims: [{ kind: "rotate", uid: null }] }
 * An animation without uid: is found by its target and kind (oscillaLive.js).
 */
export function animsOf(line) {
  const { target, expr } = splitInlineTarget(line.replace(/\s+/g, " ").trim());
  const checked = checkExpression(expr);
  if (checked.error) return { target, anims: [] };
  // shape(uid:sh1, ...) rotate(...): the line's shape is what it animates
  const leader = checked.parts.find(p => LINE_LEADERS.has(p.ast.type));
  const anims = checked.parts.filter(p => ANIM_KINDS[p.ast.type]).map(p => ({ kind: ANIM_KINDS[p.ast.type], uid: uidOf(p.ast) }));
  return { target: target ?? (leader ? uidOf(leader.ast) : null), anims };
}

/**
 * The line in the console's text that starts the synth `name`, or null --
 * what solo(name) starts it from when it has not been run yet. The last
 * one written, comments left out.
 */
export function synthLineIn(text, name) {
  let found = null;
  for (const { expr } of scanExpressions(String(text ?? ""))) {
    if (!expr.includes(name)) continue;
    const checked = checkExpression(splitInlineTarget(expr).expr);
    for (const p of checked.parts || []) if (p.ast.type === "cueSynth" && uidOf(p.ast) === name) found = p.expr;
  }
  return found;
}

/**
 * The names a line makes: { sounds, drawn } -- its synth / audio uids, and
 * what it puts on the stage (a shape, a drawing, a text, a picture, film,
 * the camera). An animation's target (x > rotate(...)) is in `targets`.
 * liveScenes.js: what a scene has, to fade and to take away.
 */
export function namesOf(line) {
  const out = { sounds: [], drawn: [], targets: [] };
  const { target, expr } = splitInlineTarget(String(line).replace(/\s+/g, " ").trim());
  if (target) out.targets.push(target);
  const checked = checkExpression(expr);
  if (checked.error) return out;
  for (const { ast } of checked.parts) {
    const uid = uidOf(ast);
    if (SOUND_TYPES.has(ast.type)) { if (uid) out.sounds.push(String(uid)); }
    else if (ast.type === "cueUse") { const u = useUid(ast.args); if (u) out.drawn.push(String(u)); }
    else if (ast.type === "cueCam") out.drawn.push(String(uid ?? "cam"));
    // an input's box (scope-chair): what it draws, faded with a scene's change
    else if (ast.type === "cueAdc" && !isOff(ast)) out.drawn.push(String(uid ?? "adc"));
    else if (["cueShape", "cueText", "cueImage", "cueVideo"].includes(ast.type) && uid) out.drawn.push(String(uid));
  }
  return out;
}

/**
 * Running everything (Ctrl+Shift+Enter): what the buffer's commented-out code
 * started, taken back -- as Ctrl+Enter on it does -- so the stage is what the
 * live lines make. Each run of // lines read as code (an expression over
 * several of them too); prose ("// Try: ...") does not parse and is left be.
 * Not what a live line makes as well (a // synth(uid:bass ...) alternative
 * leaves the playing bass alone), nor lines `skip` says (a scene's). The lines
 * to run, once each.
 */
export function commentedTakeBacks(text, activeExprs = [], skip = () => false) {
  const lines = String(text ?? "").split("\n");
  const active = new Set(activeExprs.flatMap(e => { const n = namesOf(e); return [...n.sounds, ...n.drawn]; }));
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    if (!/^\s*\/\//.test(lines[i]) || skip(i)) continue;
    let j = i;
    while (j + 1 < lines.length && /^\s*\/\//.test(lines[j + 1]) && !skip(j + 1)) j++;
    const code = lines.slice(i, j + 1).map(l => l.replace(/^\s*\/\/\s?/, "")).join("\n");
    i = j;
    for (const e of scanExpressions(code)) {
      const n = namesOf(e.expr);
      if ([...n.sounds, ...n.drawn, ...n.targets].some(x => active.has(x))) continue;
      out.push(...undoFor(e.expr));
    }
  }
  return [...new Set(out)];
}

// ---- a line run again: what it no longer says, back to its default ------------------

/** The settings a line of each of these cues leaves on what it names (the others run anew each time). */
const SETTINGS = {
  cueViz: new Set(["drag", "map", "view", "trail", "rows", "span", "step", "tween", "scroll", "width", "color", "stroke", "palette", "y", "lag", "cycle", "spin", "blend", "opacity", "glyphs"]),
  cueShape: new Set(["at", "opacity", "fill", "blend", "width", "drag", "map"]),
  cueScreen: new Set(["kaleido", "turn", "mirror", "tile", "hue", "invert", "contrast", "saturate", "bright", "blur", "pixel", "warp", "grain", "split"]),
  // pictures: only map:, put back flat with map:0 (the rest of their line shows them anew)
  cueImage: new Set(["map"]),
  cueVideo: new Set(["map"]),
  cueCam: new Set(["map"]),
};
const PICTURE = { cueImage: "image", cueVideo: "video", cueCam: "cam" };
/** screen()'s neutral values: the multiplying ones are 1, mirror off, the rest 0. */
const SCREEN_REST = { contrast: 1, saturate: 1, bright: 1, mirror: "off" };

/** A line's settings: [{ key: "cueViz|hiss", type, uid, keys }] for each viz / shape / screen in it. */
export function lineSettings(line) {
  const { target, expr } = splitInlineTarget(String(line ?? "").replace(/\s+/g, " ").trim());
  const checked = checkExpression(expr);
  if (checked.error) return [];
  const out = [];
  for (const { ast } of checked.parts) {
    const set = SETTINGS[ast.type];
    if (!set) continue;
    const uid = ast.type === "cueScreen" ? "stage" : (uidOf(ast) ?? target);
    if (uid == null) continue;
    // video() keeps its settings as params, the others as args
    const said = ast.args ? ast.args.map(a => a.type) : Object.keys(ast.params || {});
    const keys = new Set(said.filter(k => set.has(k)));
    out.push({ key: `${ast.type}|${uid}`, type: ast.type, uid: String(uid), keys });
  }
  return out;
}

/**
 * The same line run again (the console keeps what each of its lines last
 * set): the settings it said before and says no longer, as the lines that put
 * them back -- viz(s, unset:[scroll, step]), shape(uid:x, unset:[at]),
 * screen(warp:0). `before`: key -> the keys it set then.
 */
export function resetsFor(before, now) {
  const out = [];
  for (const s of now) {
    const was = before.get(s.key);
    if (!was) continue;
    const gone = [...was].filter(k => !s.keys.has(k));
    if (!gone.length) continue;
    if (s.type === "cueViz") out.push(`viz(${s.uid}, unset:[${gone.join(", ")}])`);
    else if (s.type === "cueShape") out.push(`shape(uid:${s.uid}, unset:[${gone.join(", ")}])`);
    else if (PICTURE[s.type]) out.push(`${PICTURE[s.type]}(uid:${s.uid}, map:0)`);
    else out.push(`screen(${gone.map(k => `${k}:${SCREEN_REST[k] ?? 0}`).join(", ")})`);
  }
  return out;
}

/**
 * The lines that take `line` back, or [] when there is nothing to stop.
 *   shape(uid:sh1 ...) ...       -> shape(uid:sh1, remove:1)   (its sounds and animations go with it)
 *   use(orion ...) ...           -> use(uid:orion, remove:1)
 *   synth(uid:s2 ...)            -> hush(uid:[s2])
 *   text(uid:sz ...)             -> text(uid:sz, stop:1)
 *   vu(bass ...)                 -> vu(uid:vu, stop:1)
 *   organ > rotate(...)          -> organ > rotate(off:1)
 */
export function undoFor(line) {
  const { target, expr } = splitInlineTarget(line.replace(/\s+/g, " ").trim());
  const checked = checkExpression(expr);
  if (checked.error) return [];
  const [first] = checked.parts;
  if (first.ast.type === "cueShape") {
    const uid = uidOf(first.ast);
    return uid ? [`shape(uid:${uid}, remove:1)`] : [];
  }
  if (first.ast.type === "cueUse") {
    const uid = useUid(first.ast.args);
    return uid ? [`use(uid:${uid}, remove:1)`] : [];
  }
  const out = [], sounds = [];
  // a line run on a signal (trig:s1.hit): stopped watching
  const trig = checked.parts[0]?.ast?.args?.find(a => a.type === "trig")?.value;
  if (typeof trig === "string" && /\./.test(trig)) {
    return [line.replace(/\btrig\s*:\s*("[^"]*"|'[^']*'|every\s*\([^)]*\)|[^,)\s]+)/, "trig:off")];
  }
  for (const { ast } of checked.parts) {
    const uid = uidOf(ast);
    if (SOUND_TYPES.has(ast.type) && uid) sounds.push(uid);
    else if (ast.type === "cueText" && uid) out.push(`text(uid:${uid}, stop:1)`);
    else if (ast.type === "cueImage" && uid) out.push(`image(uid:${uid}, remove:1)`);
    else if (ast.type === "cueVideo" && uid) out.push(`video(uid:${uid}, remove:1)`);
    else if (ast.type === "cueCam") out.push(`cam(uid:${uid ?? "cam"}, off:1)`);
    // an input opened: closed again, and its box with it (adc(off:1) alone lets go of the microphone)
    else if (ast.type === "cueAdc" && !isOff(ast)) out.push(uid ? `adc(uid:${uid}, off:1)` : "adc(off:1)");
    else if (ast.type === "cueVu") out.push(`vu(uid:${uid ?? "vu"}, stop:1)`);
    else if (ast.type === "cueBang") {
      // a metro stopped; a single bang has nothing to take back
      const names = ast.args.find(a => a.type === "names")?.value || [];
      if (names.length && ast.args.some(a => ["value", "every", "numbers"].includes(a.type))) out.push(`bang([${names.join(", ")}], 0)`);
    }
    else if (ANIM_NAMES[ast.type] && target) out.push(`${target} > ${ANIM_NAMES[ast.type]}(off:1)`);
  }
  if (sounds.length) out.unshift(`hush(uid:[${sounds.join(", ")}])`);
  return out;
}

// ---- a value the stage sets, written into the line that says it -------------

/**
 * The top-level arguments of the call whose "(" is at `open`:
 * { close, args: [{ start, end, key }] } -- each argument's span (trimmed),
 * `key` its name before a ":" (null for a bare value). Quotes, brackets and
 * // comments skipped. null when the call is not closed.
 */
function callArgs(text, open) {
  const args = [];
  let depth = 0, quote = "", start = open + 1;
  const push = (end) => {
    let a = start, b = end;
    // leading space, and a // comment ending the line before
    for (;;) {
      while (a < b && /\s/.test(text[a])) a++;
      if (text[a] !== "/" || text[a + 1] !== "/") break;
      const nl = text.indexOf("\n", a);
      a = nl < 0 || nl > b ? b : nl;
    }
    while (b > a && /\s/.test(text[b - 1])) b--;
    if (b > a) args.push({ start: a, end: b, key: text.slice(a, b).match(/^([A-Za-z_]\w*)\s*:/)?.[1] ?? null });
  };
  for (let i = open; i < text.length; i++) {
    const ch = text[i];
    if (quote) { if (ch === quote) quote = ""; continue; }
    if (ch === '"' || ch === "'") { quote = ch; continue; }
    if (ch === "/" && text[i + 1] === "/") { const nl = text.indexOf("\n", i); if (nl < 0) return null; i = nl; continue; }
    if (ch === "(" || ch === "[" || ch === "{") depth++;
    else if (ch === ")" || ch === "]" || ch === "}") {
      depth--;
      if (depth === 0) { push(i); return { close: i, args }; }
    } else if (ch === "," && depth === 1) { push(i); start = i + 1; }
  }
  return null;
}

/**
 * Where to write `key:value` for the thing named `uid`, made by one of
 * `cues` (in the order they are preferred): { from, to, insert } -- the span
 * of the text to replace. A call naming it (uid:spot, or viz(spot ...)'s
 * first word; with no name, the cue's own: cam()) that already says the key
 * has its value replaced; otherwise
 * the key goes at the end of the first call naming it, by the first cue
 * that does. null when no line names it, or (plain) when the value said is
 * code -- a follow(), a sinOsc(). Lines starting with // or # are passed by.
 */
export function writeKeyPlan(text, { uid, key, value, cues, plain = false }) {
  const named = [];
  const lineStart = i => text.lastIndexOf("\n", i - 1) + 1;
  for (const call of topLevelCalls(text)) {
    if (!cues.includes(call.name)) continue;
    if (/^\s*(\/\/|#)/.test(text.slice(lineStart(call.open), call.open))) continue;
    const parsed = callArgs(text, call.open);
    if (!parsed) continue;
    const said = parsed.args.find(a => a.key === "uid");
    const bare = parsed.args[0] && !parsed.args[0].key ? text.slice(parsed.args[0].start, parsed.args[0].end) : null;
    // no name written: the cue's own (cam() is cam, image() is image)
    const name = said ? text.slice(said.start, said.end).replace(/^uid\s*:\s*["']?|["']$/g, "") : bare ?? call.name;
    const names = name === uid;
    if (names) named.push({ ...call, ...parsed });
  }
  if (!named.length) return null;
  const saying = named.find(c => c.args.some(a => a.key === key));
  if (saying) {
    const a = saying.args.find(x => x.key === key);
    // plain: a value that is code (at:[follow(mouse.x), 0.5]) is left as written
    if (plain && /\(/.test(text.slice(a.start, a.end))) return null;
    return { from: a.start, to: a.end, insert: `${key}:${value}` };
  }
  const first = cues.map(c => named.find(n => n.name === c)).find(Boolean);
  const last = first.args[first.args.length - 1];
  const at = last ? last.end : first.open + 1;
  return { from: at, to: at, insert: last ? `, ${key}:${value}` : `${key}:${value}` };
}
